import { useContext, useMemo, useRef, useState } from "react";
import { useColsEffect } from "../../utils/tables";
import { icon } from "@fortawesome/fontawesome-svg-core";
import { faEdit, faInfoCircle, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReportDetailDialog } from "./ReportDetailDialog";
import { dateFormat } from "../../utils/date";
import PromiseWrapper from "../Utils/PromiseDialog";
import DivPortal from "../Utils/DivPortal";
import RestAPI from "@aws-amplify/api-rest";
import { useLocation } from "react-router";
import { toast } from 'react-toastify';
import { FarmContext } from "../../contexts/FarmContext";
import DataTablePrime from "../PrimeComponents/DataTablePrime";
import { FilterMatchMode } from "primereact/api";
import TooltipPrime from "../PrimeComponents/TooltipPrime";
import BulkEditBtnPrime from "../PrimeComponents/BulkEditBtnPrime";


const getFarm = (location) => {
    const params = new URLSearchParams(location && location.search);
    const farm = params.get('farm');
    return farm;
}


/**
* @typedef {{ 
*   reporte_id: number, nombre: string, cuenta_id: number, campo_id: number,
*   parametros: {
*       sector_id?: number, sectores?: number[], equipo_id?: number[], periodo_reporte: string, tipo_reporte: string
*   },
*   configuracion: { destinatarios: string[], expression: string, timezone: string, language: string, fecha_ultimo_envio: string,
*       cron_config: {
*           custom: boolean, freq?: string, selector?: string, 
*           week?: string, day_week?: number, day?: number, hour?: number
*        }
*   },
*   esta_habilitado: Boolean
* }} Report
*/

/**
 * @param {{ reports: Report[] }} reports
 */
const ReportsTable = ({ reports, setReportDialogState, setFarmReports, sectorsInfo, fields, setReFetch, usuarioInfo }) => {
    const { t, i18n } = useTranslation();
    const gridRef = useRef(null);
    const promiseWrapper = useRef(null);
    const [reportDetailState, setReportDetailState] = useState({ visible: false })
    const [bulkState, setBulkState] = useState(null);
    const location = useLocation();
    const farmId = getFarm(location);
    const [_, dispatch] = useContext(FarmContext);
    const canEdit = dispatch({ type: "get", path: "canEdit" });
    const [showSelectAll, setShowSelectAll] = useState(false);
    const [selectedReports, setSelectedReports] = useState(false);

    const names = useMemo(() => [
      t("general.name"),
      t("general.type"),
      t("automaticReports.timeRange"),
      t("automaticReports.frequency"),
      t("automaticReports.lastSent"),
      t("forms.tituloParametrosGenerales")
    ], [t]);

    const allPeriods = useMemo(() => [
        { key: "1 week window", label: "1 "+ t("automaticReports.week")  }, 
        { key: "2 week window", label: "2 " + t("automaticReports.weeks") },
        { key: "1 month window", label: "1 " + t("automaticReports.month") },
        { key: "3 month window", label: "3 " + t("automaticReports.months") },
        { key: "6 month window", label: "6 " + t("automaticReports.months") },
        { key: "1 year window", label: "1 " + t("automaticReports.year") },
        { key: "2 year window", label: "2 " + t("automaticReports.years") },
        { key: "3 year window", label: "3 " + t("automaticReports.years") },
        { key: "1 day", label: t("automaticReports.today")   }, // from 00:00 to the date hour
        { key: "1 week", label: t("automaticReports.thisWeek")  }, // from <mondayDate>/<month> to the date
        { key: "1 month", label: t("automaticReports.thisMonth")  }, // from 01/<month> to the date
        { key: "1 year", label: t("automaticReports.thisYear")  }, // from 01/01 to the date
    ], [t]);
    
    const frecuenciaEnvioOptions = useMemo(() =>[
        { key: 'daily', label: t("automaticReports.daily") },
        { key: 'weekly', label: t("automaticReports.weekly") },
        { key: 'monthly', label: t("automaticReports.monthly") },
        { key: 'annually', label: t("automaticReports.yearly") },
    ], [t]);
  
    // mutate table data
    const dataSource = useMemo(() => {
        return reports.map((r, id) => {
            const fechaEnvio = r.configuracion.fecha_ultimo_envio;
            const createdByUser = r.configuracion?.usuario_id === usuarioInfo?.usuario_id;
            const isNotEditable = !createdByUser && !canEdit;
            if (!isNotEditable) setShowSelectAll(true);
            return {
                ...r,
                tipo_reporte_i18n: t(`automaticReports.tipo.${r.parametros.tipo_reporte}`),
                periodo_reporte: allPeriods.find(p => p.key === r.parametros.periodo_reporte)?.label ?? "",
                periocidad_envio: frecuenciaEnvioOptions.find(p => p.key === r.configuracion.cron_config.freq)?.label ?? "",
                sectores: r.parametros.sectores || r.parametros.sector_id,
                equipos: r.parametros.equipos || r.parametros.equipo_id,
                campos: r.parametros.campos,
                fecha_ultimo_envio: fechaEnvio ? dateFormat(r.configuracion.fecha_ultimo_envio, t, i18n.language, r.configuracion.timezone) : "",
                isNotEditable
            }
        });
    }, [reports, t, allPeriods, frecuenciaEnvioOptions, i18n.language, canEdit, usuarioInfo?.usuario_id]);

    const rowSelection = (value) => {
        setSelectedReports(value);
        const onlyEditable = value.filter(r => !r.isNotEditable);
        if (!value?.length) setBulkState(p => ({ ...p, showBulkEdit: false }));
        else setBulkState(p => ({ ...p, showBulkEdit: true, rows: onlyEditable }));
    }
    
    const heightTable = Array.isArray(dataSource) &&
      (dataSource.length < 10 ? `${dataSource.length * 100}px` : '500px');
  
      
      const TemplateParametros = (reporte) => {
          return (
            <TooltipPrime id="report-toolSelfHelp"  content={t("systemEvents.table.detalle")} position="bottom">
                <FontAwesomeIcon 
                    className='pointer' 
                    onClick={() => setReportDetailState({ 
                        visible: true, 
                        reporte,
                        sectorsInfo,
                        fields
                    })} 
                    size="lg" icon={faInfoCircle} style={{ color: 'lightblue' }}
                 />
            </TooltipPrime>
        )
    }

    const onEdit = (rowData) => {
        const data = {
            nombre: rowData.nombre,
            periodo_reporte: rowData.parametros.periodo_reporte,
            tipo_reporte: rowData.parametros.tipo_reporte,
            sectores: rowData.parametros.sectores,
            equipos: rowData.parametros.equipos,
            campos: rowData.parametros.campos,
            mails_envio_reporte: rowData.configuracion.destinatarios,
            freq: rowData.configuracion.cron_config.freq,
            monthly_check: rowData.configuracion.cron_config.monthly_check,
            time: rowData.configuracion.cron_config.time,
            day_of_week: rowData.configuracion.cron_config.day_of_week,
            occurrence_in_week: rowData.configuracion.cron_config.occurrence_in_week,
            day_of_month: rowData.configuracion.cron_config.day_of_month,
            day_of_year: rowData.configuracion.cron_config.day_of_year
        }
        setReportDialogState({ visible: true, data, rowData, editMode: true, reporteId: rowData.reporte_id, setReFetch });
    }

    const onDelete = (rowData) => {
        promiseWrapper.current.getPromiseAnswer().then((userAcceptsDelete) => {
            if (!userAcceptsDelete) return;
            setFarmReports(reports.filter(r => r.reporte_id !== rowData.reporte_id));
            if (gridRef?.current) gridRef.current.deleteRecord('reporte_id', [rowData.reporte_id]);
            
            const reporteId = rowData.reporte_id;
            const endpoint = `/farms/${farmId}/reporte/${reporteId}/eliminar`;
            RestAPI.post('apiAdminDropControl', endpoint).then(() => {
                toast.success(t("forms.toastSuccess"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
            }).catch(() => {
                toast.error(t("forms.toastError"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
                setReFetch(p => p + 1);
            })
        });
        
    }

    useColsEffect(gridRef, names, t);
    return (
        <div>
            <PromiseWrapper
                ref={promiseWrapper}
                yesText={t("general.answerYes")} noText={t("general.answerNo")}
                headerText={t("forms.delete")} contentText={t("sdb.eliminar.msg")}
            />

            {
                bulkState?.showBulkEdit && (
                <BulkEditBtnPrime enabled={bulkState?.showBulkEdit} items={[{ text: t("farms.pozo.reportesEmails"), id: "mails_envio_reporte" }]} divId="reportsBulkContainer" onClickBulk={(e) => {
                    setReportDialogState({ 
                        visible: true, 
                        data: {}, 
                        bulkInfo: {
                            bulkEnabled: true, 
                            fieldToEdit: "mails_envio_reporte", 
                            selectedRows: selectedReports
                        }
                    });
                }} />
                )
            }
            <DataTablePrime
                t={t}
                scrollHeight={heightTable}
                editableRow={true}
                selectionMode={!dataSource?.[0].isNotEditable?"checkbox":null}
                selection={selectedReports}
                onSelectionChange={(e) => rowSelection(e.value)}
                editFunction = {async (event, rowData) => await onEdit(rowData)}
                deletableRow={true}
                deleteFunction = {async (event, rowData) => await onDelete(rowData)}
                filters={{
                    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
                }}
                rowDataBoundFunction= {(rowData) => {
                  if (!rowData?.isNotEditable) return true;
                  else return false;
                }}
                items={dataSource}
                size="small"
                header={dataSource?.length > 5 ? "search":null}
                globalFilterFields={["nombre"]}
                fields={[
                    {field:"reporte_id",header:t("farms.pozo.reportesEmails"), hidden:true, bodyStyle:{textAlign:"center"}},
                    {field:"nombre",header:names[0], bodyStyle:{textAlign:"center"}},
                    {field:"tipo_reporte_i18n",header:names[1], bodyStyle:{textAlign:"center"}},
                    {field:"periodo_reporte",header:names[2], bodyStyle:{textAlign:"center"}},
                    {field:"periocidad_envio",header:names[3], bodyStyle:{textAlign:"center"}},
                    {field:"fecha_ultimo_envio",header:names[4], bodyStyle:{textAlign:"center"}},
                    {field:"parametros_col",body:TemplateParametros, header:names[5], bodyStyle:{textAlign:"center"}},
                ]}
            />
            {
                reportDetailState?.visible && (
                    <ReportDetailDialog 
                        dialogState={reportDetailState} 
                        setDialogState={setReportDetailState} 
                        sectorsInfo={sectorsInfo}
                        fields={fields}
                    />
                )
            }
        </div>
    );
  };

export { ReportsTable }